import type { PlayerWithMarkets, BookLine, BestLine, BestLines, HiddenBy, MarketCode } from '@sharpr/types';
import { computeBestMarket } from './bestLineCalculator';

/**
 * Strip all projection-related data from players, leaving only odds.
 * Used when construction mode is enabled for non-admin users.
 * The frontend already handles undefined EV/projection gracefully (partial data display).
 */
export function stripProjectionData(players: PlayerWithMarkets[]): PlayerWithMarkets[] {
  return players.map(player => ({
    ...player,
    projectedMinutes: undefined,
    bestMarket: undefined,
    teamNotes: undefined,
    playerNotes: undefined,
    injury: undefined,
    markets: stripMarkets(player.markets),
  }));
}

type MarketLike = {
  projection: number | null;
  books: BookLine[];
  bestLines: BestLines;
  hiddenBy?: HiddenBy[];
};

/**
 * Strip projection/EV data from a single market entry while preserving odds.
 * Generic over the market shape (projectedMinutes / teamNotes are optional).
 * Also clears the admin-only `hiddenBy` marker — non-admins must not learn
 * a hidden projection exists.
 */
export function stripMarketProjection<T extends MarketLike>(data: T): T {
  return {
    ...data,
    projection: null,
    projectedMinutes: undefined,
    teamNotes: undefined,
    pmf: undefined,
    hiddenBy: undefined,
    books: data.books.map(stripBookLine),
    bestLines: {
      over: data.bestLines.over ? stripBestLine(data.bestLines.over) : undefined,
      under: data.bestLines.under ? stripBestLine(data.bestLines.under) : undefined,
    },
  };
}

function stripMarkets(markets: PlayerWithMarkets['markets']): PlayerWithMarkets['markets'] {
  const stripped: PlayerWithMarkets['markets'] = {};
  for (const [code, data] of Object.entries(markets)) {
    if (!data) continue;
    stripped[code as MarketCode] = stripMarketProjection(data);
  }
  return stripped;
}

function stripBookLine(book: BookLine): BookLine {
  const { ev: _ev, pWin: _pWin, ...rest } = book;
  return rest;
}

function stripBestLine(line: BestLine): BestLine {
  const { ev: _ev, pWin: _pWin, ...rest } = line;
  return rest;
}

export interface StripForPublicViewOptions {
  hiddenTeams: string[];
  hiddenMarkets: string[];
}

/**
 * Apply the hidden-team / hidden-market / analyst-hidden strip used for non-admin
 * viewers. Hidden-team players keep every market with projection/EV nulled out;
 * hidden-market AND `hiddenBy`-marked markets (MLB PMF hides, blob-priced
 * for admins only) have projection/EV nulled out in place; unaffected markets
 * pass through by reference. A stripped player's bestMarket is recomputed
 * (`computeBestMarket`) so it keeps a stable rank — pre-sorted cache ordering is
 * preserved (see `getCurrentOddsBySportPaginated`).
 *
 * Shared between the server (`database.ts` at paginated-read time) and the client
 * admin-preview (`PlayerSelectionView.tsx` when `adminView=false`) so the two paths
 * can't diverge.
 */
export function stripForPublicView(
  players: PlayerWithMarkets[],
  opts: StripForPublicViewOptions,
): PlayerWithMarkets[] {
  const teamSet = opts.hiddenTeams.length > 0 ? new Set(opts.hiddenTeams) : null;
  const marketSet = opts.hiddenMarkets.length > 0 ? new Set(opts.hiddenMarkets) : null;

  let anyTouched = false;
  const out = players.map(player => {
    if (teamSet?.has(player.team)) {
      const newMarkets: PlayerWithMarkets['markets'] = {};
      for (const [code, data] of Object.entries(player.markets)) {
        if (!data) continue;
        newMarkets[code as MarketCode] = stripMarketProjection(data);
      }
      anyTouched = true;
      return {
        ...player,
        projectedMinutes: undefined,
        teamNotes: undefined,
        playerNotes: undefined,
        markets: newMarkets,
        bestMarket: computeBestMarket(newMarkets),
      };
    }

    let touched = false;
    const newMarkets: PlayerWithMarkets['markets'] = {};
    for (const [code, data] of Object.entries(player.markets)) {
      if (!data) continue;
      if (marketSet?.has(code) || data.hiddenBy?.length) {
        newMarkets[code as MarketCode] = stripMarketProjection(data);
        touched = true;
      } else {
        newMarkets[code as MarketCode] = data;
      }
    }
    if (touched) {
      anyTouched = true;
      return {
        ...player,
        markets: newMarkets,
        bestMarket: computeBestMarket(newMarkets),
      };
    }

    return player;
  });
  // Untouched input passes through by reference (memo-friendly for the client preview).
  return anyTouched ? out : players;
}

/**
 * MLB's admin markers onto its rows (phase 2: the seed's rows carry them, as NBA's do) — the admin view's mirror of
 * `stripForPublicView`: a hidden team marks every market of its players `team`, a hidden market marks that market
 * `sport`, beside any `player` hide the server stamped. Unmarked players pass through by reference.
 */
export function withHiddenBy(players: PlayerWithMarkets[], hiddenTeams: readonly string[], hiddenMarkets: readonly string[]): PlayerWithMarkets[] {
  if (hiddenTeams.length === 0 && hiddenMarkets.length === 0) return players;
  const teams = new Set(hiddenTeams);
  const markets = new Set(hiddenMarkets);
  return players.map(p => {
    const team = teams.has(p.team);
    if (!team && !Object.keys(p.markets).some(m => markets.has(m))) return p;
    const marked = Object.entries(p.markets).map(([market, d]) => {
      const hiddenBy: HiddenBy[] = [...(team ? ['team' as const] : []), ...(markets.has(market) ? ['sport' as const] : []), ...(d?.hiddenBy ?? [])];
      return [market, d && hiddenBy.length > 0 ? { ...d, hiddenBy } : d];
    });
    return { ...p, markets: Object.fromEntries(marked) as PlayerWithMarkets['markets'] };
  });
}
